<script setup lang="ts">
import { computed, ref, watch, onMounted, inject } from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useCharacterDataStore } from "@/stores/character-data";
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import { useGuidedElementPicker } from "@/composables/useGuidedElementPicker";
import { useEquipmentStatus } from "@/composables/useEquipmentStatus";
import { useFormatModifier } from "@/composables/useFormatModifier";
import ElementBrowser from "@/components/atoms/swade/ElementBrowser.vue";
import GuidedScopeFilters from "@/components/atoms/element-filters/GuidedScopeFilters.vue";
import FilterControl from "@/components/atoms/element-filters/FilterControl.vue";
import ItemTypeFilter from "@/components/atoms/element-filters/ItemTypeFilter.vue";
import { buildSecondaryChips, clearSecondaryChip } from "@/composables/useElementPredicates";
import CurrencyAdjuster from "@/components/characters/modals/CurrencyAdjuster.vue";
import type { SwadeCharacterInterface } from "@/types/characters";
import type { SettingElementInterface, SwadeItemInterface, SwadeItemContainerInterface } from "@/types/items";
import type { ColumnConfig, FilterChip } from "@/types/browser";
import type { IntlService } from "@progress/kendo-vue-intl";
import { containerDisplayName } from '@/appdata/container-fields';
import { useCurrencyAdjuster } from '@/composables/useCurrencyAdjuster';

const props = defineProps<{ stepIndex: number; }>();
const character = defineModel<SwadeCharacterInterface | null>('character');
const kendo: IntlService = inject('kendo') as IntlService;

const ux = useUxData();
const characterData = useCharacterDataStore();
const setupStore = useGuidedSetupStore();
const { statusIcon, statusesForType } = useEquipmentStatus();
const { formatModifier } = useFormatModifier();
const { opensFor } = useCurrencyAdjuster();

const currencyContext = ref<SwadeItemContainerInterface | null>(null);

const {
  browserRef, open, onSelect,
  dataItems, selectedElement, loading, metadata, links,
  onSearch, onNextPage, onPreviousPage, filters,
  showWorldFilter, filtersActive, clearFilters,
} = useGuidedElementPicker('items', {
  alias: 'items',
  character: () => character.value,
});

const equipment = computed(() => character.value?.equipment ?? []);

const selectedContainer = computed(() => {
  if (!selectedElement.value) return null;
  return equipment.value.find((c: SwadeItemContainerInterface) => c.selection?.uuid === selectedElement.value?.uuid) ?? null;
});

const initialFunds = computed(() => characterData.buildOptions?.startingFunds?.amount ?? 500);
const currentFunds = computed(() => typeof character.value?.purchasingPower === 'number' ? character.value.purchasingPower : 0);
const totalSpent = computed(() => Math.max(0, initialFunds.value - currentFunds.value));
const currencySymbol = computed(() => characterData.buildOptions?.startingFunds?.currencySymbol ?? '$');

watch(() => true, () => { setupStore.validateStep(props.stepIndex, true); }, { immediate: true });

// ElementBrowser (and the ElementGrid inside it) declares every element-shaped prop, emit and slot
// on the shared SettingElementInterface, because one browser serves every element type. This step
// browses ITEMS only: `useGuidedElementPicker('items', …)` above fetches exclusively from the API's
// items collection (`alias: 'items'`), and
// `onSelect`'s hydration re-fetches the same element. So the step narrows at its own boundary
// rather than making the shared browser generic — a generic ElementBrowser would have to be matched
// by a generic ElementGrid, which some twenty other consumers (finders, data lists, WorldRulesStep)
// bind to.
//
// The truthiness test is the same one the #details slot already ran as `v-if="element"`, so the
// guard narrows the type without changing which branch renders.
const isItem = (element: SettingElementInterface | null | undefined): element is SwadeItemInterface =>
  !!element;

// Keyed off `uuid` alone, so these take the base element type the browser hands back: an element
// that is not in this character's equipment simply matches no container.
const isItemAdded = (item: SettingElementInterface): boolean =>
  equipment.value.some((c: SwadeItemContainerInterface) => c.selection?.uuid === item.uuid);
const isAdded = (item: SettingElementInterface) => isItemAdded(item);
const canAdd = () => true; // items stack

const itemCount = (item: SettingElementInterface): number =>
  equipment.value
    .filter((c: SwadeItemContainerInterface) => c.selection?.uuid === item.uuid)
    .reduce((sum: number, c: SwadeItemContainerInterface) => sum + (c.quantity ?? 1), 0);

const getItemTypeLabel = (type: string | null): string => {
  if (!type) return '';
  const labels: Record<string, string> = {
    weapon: ux.labels?.swade?.data?.equipment?.types?.weapon ?? 'Weapon',
    armor: ux.labels?.swade?.data?.equipment?.types?.armor ?? 'Armor',
    gear: ux.labels?.swade?.data?.equipment?.types?.gear ?? 'Gear',
    shield: ux.labels?.swade?.data?.equipment?.types?.shield ?? 'Shield',
  };
  return labels[type] ?? type;
};

const columns = computed<ColumnConfig[]>(() => [
  { field: 'name', header: ux.labels?.swade?.data?.equipment?.name ?? 'Name',
    columnClass: ['name-column'] },
  { field: 'type', header: ux.labels?.swade?.data?.equipment?.type ?? 'Type', formatter: (el: any) => getItemTypeLabel(el.type) },
  { field: 'source.name', header: ux.labels?.swade?.data?.equipment?.source ?? 'Source', formatter: (el: any) => el.source?.name ?? '' },
]);

const itemTypes = computed<string[]>({
  get: () => (filters.value as any).types ?? [],
  set: (value) => { (filters.value as any).types = value; },
});

// Chips live with their controls: SourceFilterControl owns the source chip, and the
// item-type chips come from the shared per-type mapping the data lists and the world
// browser read, so the wording matches everywhere.
const typeChips = computed<FilterChip[]>(() => buildSecondaryChips('items', filters.value as any));
const removeType = (chip: FilterChip) => clearSecondaryChip('items', filters.value as any, chip);

const onAdd = async (element: SettingElementInterface) => {
  if (!character.value) return;
  // Same narrowing as `isItem` above: ElementBrowser's `add` emit is declared on the shared base
  // type, but every row this step can add came from the items collection.
  const item = element as SwadeItemInterface;
  const container = await characterData.addItemToCharacter(character.value, item);
  // The adjuster opens for the container the add filled — never for a cancelled add (spec 2026-10-13
  // §7) — and only when there is money to move, here and on removal below: never for a free item
  // (owner's ruling 2026-09-28), never in a build that uses a wealth die (2026-10-08) — though the
  // setup flow skips this step for those builds anyway.
  if (container && opensFor(container.selection)) currencyContext.value = container;
};
const onRemoveItem = async (container: SwadeItemContainerInterface) => {
  if (!character.value) return;
  // Awaited, so the container is off the sheet before the adjuster opens: it refunds the money and
  // writes no quantity.
  await characterData.removeItem(character.value, container);
  if (opensFor(container.selection)) currencyContext.value = container;
};
const onRemoveSelectedItem = () => {
  if (!selectedElement.value || !character.value) return;
  const container = equipment.value.find((c: SwadeItemContainerInterface) => c.selection?.uuid === selectedElement.value?.uuid);
  if (container) onRemoveItem(container);
};
const onContainerStatusChange = (status: string) => {
  if (!selectedContainer.value || !character.value) return;
  selectedContainer.value.status = status;
  characterData.recalculateDerivedStats(character.value);
};
const onContainerQuantityChange = () => {
  if (!selectedContainer.value || !character.value) return;
  selectedContainer.value.quantity = Math.floor(selectedContainer.value.quantity);
  if (selectedContainer.value.quantity < 1) selectedContainer.value.quantity = 1;
  characterData.recalculateDerivedStats(character.value);
};
const onCustomNameChange = (container: SwadeItemContainerInterface) => {
  if ((container.name ?? "").length === 0) container.name = null;
};
const onCurrencyAdjusted = () => { currencyContext.value = null; };

onMounted(() => { setupStore.validateStep(props.stepIndex, true); });

// Template note (the #details slot's `v-if="isItem(element)"`) — kept out of the template so it emits no comment vnode:
// `isItem` is the truthiness test this v-if already performed, plus the item narrowing
// the browser's shared element type cannot express. See its definition above.
</script>

<template>
  <div class="character-gear-step">
    <div class="form-group mb-3">
      <label class="form-label fw-semibold" v-html="ux.labels?.guidedSetup?.character?.gearLabel"></label>
      <div class="d-flex flex-fill gap-2 mb-2">
        <span class="icon mb-auto"><span class="fa-light fa-info-circle"></span></span>
        <p class="form-text my-auto" v-html="ux.labels?.guidedSetup?.character?.gearHelp"></p>
      </div>

      <div class="funds-tracker alert mb-3" :class="currentFunds >= 0 ? 'alert-info' : 'alert-danger'">
        <div class="d-flex justify-content-between align-items-center">
          <span>
            <span class="fa-light fa-coins me-2"></span>
            <span v-html="ux.labels?.guidedSetup?.character?.startingFunds"></span>
          </span>
          <span class="badge fs-6" :class="currentFunds >= 0 ? 'bg-primary' : 'bg-danger'">
            <span class="fa-light" :class="currencySymbol"></span>{{ currentFunds }}
          </span>
        </div>
        <small class="d-block mt-1" v-html="kendo.format(ux.labels?.guidedSetup?.character?.spentFormatString, currencySymbol, totalSpent, initialFunds)"></small>
      </div>

      <div v-if="equipment.length > 0" class="equipment-list d-flex flex-md-row flex-column flex-fill flex-wrap gap-2 mb-3">
        <div v-for="container in equipment" :key="container.uuid ?? container.selection?.uuid" class="card card-body gear-item">
          <div class="d-flex justify-content-between flex-fill">
            <div class="item-name fw-semibold my-auto">
              {{ containerDisplayName(container) }}
              <span v-if="container.quantity > 1" class="badge bg-info text-white me-1">x{{ container.quantity }}</span>
            </div>
            <button type="button" class="btn btn-sm btn-link text-danger" @click="onRemoveItem(container)">
              <span class="fa-light fa-times"></span>
            </button>
          </div>
          <div class="d-flex gap-3 align-items-center">
            <span v-if="container.selection?.type" class="badge bg-secondary text-white">{{ getItemTypeLabel(container.selection.type) }}</span>
            <span v-if="container.selection?.cost" class="text-muted small"><span class="fa-light" :class="currencySymbol"></span>{{ container.selection.cost }}</span>
          </div>
        </div>
      </div>

      <button type="button" class="btn btn-primary w-100 d-lg-none mb-3 add-trigger" @click="open()">
        <span class="fa-light fa-plus"></span>
        {{ ux.labels?.guidedSetup?.character?.addGear ?? 'Add Item' }}
      </button>

      <ElementBrowser
        ref="browserRef"
        :inline="true"
        :close-on-add="true"
        :title="ux.labels?.guidedSetup?.character?.addGear ?? 'Add Item'"
        :data-items="dataItems"
        :columns="columns"
        :selected-element="selectedElement"
        :is-added="isAdded"
        :can-add="canAdd"
        :loading="loading"
        :metadata="metadata"
        :links="links"
        :filters-active="filtersActive"
        @add="onAdd"
        @select="onSelect"
        @search="onSearch"
        @next="onNextPage"
        @previous="onPreviousPage"
        @clear="clearFilters"
      >
        <template #filters>
          <GuidedScopeFilters :show-world="showWorldFilter" />
          <FilterControl
            :label="ux.labels?.swade?.data?.equipment?.type ?? 'Type'"
            :chips="typeChips"
            :multiple="true"
            @remove="removeType"
          >
            <ItemTypeFilter
              v-model:types="itemTypes"
              :label="ux.labels?.swade?.data?.filters?.labels?.itemType ?? 'Item Type'"
              :placeholder="ux.labels?.swade?.data?.filters?.category ?? 'Category'"
            />
          </FilterControl>
        </template>

        <template #details="{ element }">
          <template v-if="isItem(element)">
            <div class="gear-details">
              <div class="d-flex flex-fill gap-2">
                <div v-if="element.cost != null" class="d-flex gap-2 item-cost ms-auto">
                  <span class="label fw-semibold" v-html="ux.labels.swade.data.equipment.details.cost"></span>
                  <span><span class="fa-light" :class="currencySymbol"></span>{{ element.cost }}</span>
                </div>
              </div>

              <div v-if="element.details" class="d-flex flex-fill item-detail-list details gap-2 flex-wrap justify-content-between mb-3">
                <div v-if="element.details.damage" class="damage d-flex flex-column">
                  <label class="d-block text-center fw-semibold">{{ ux.labels?.swade?.data?.equipment?.details?.damage ?? 'Damage' }}</label>
                  <span class="d-block text-center value">{{ element.details.damage }}</span>
                </div>
                <div v-if="element.details.range" class="range d-flex flex-column">
                  <label class="d-block text-center fw-semibold">{{ ux.labels?.swade?.data?.equipment?.details?.range ?? 'Range' }}</label>
                  <span class="d-block text-center value">{{ element.details.range }}</span>
                </div>
                <div v-if="element.details.rateOfFire" class="rate-of-fire d-flex flex-column">
                  <label class="d-block text-center fw-semibold">{{ ux.labels?.swade?.data?.equipment?.details?.rateOfFire ?? 'ROF' }}</label>
                  <span class="d-block text-center value">{{ element.details.rateOfFire }}</span>
                </div>
                <div v-if="element.details.ap" class="ap d-flex flex-column">
                  <label class="d-block text-center fw-semibold">{{ ux.labels?.swade?.data?.equipment?.details?.ap ?? 'AP' }}</label>
                  <span class="d-block text-center value">{{ element.details.ap }}</span>
                </div>
                <div v-if="element.details.armor" class="armor-value d-flex flex-column">
                  <label class="d-block text-center fw-semibold">{{ ux.labels?.swade?.data?.equipment?.details?.armorValue ?? 'Armor' }}</label>
                  <span class="d-block text-center value">{{ formatModifier(element.details.armor) }}</span>
                </div>
                <div v-if="element.details.parry" class="parry-modifier d-flex flex-column">
                  <label class="d-block text-center fw-semibold">{{ ux.labels?.swade?.data?.equipment?.details?.parryValue ?? 'Parry' }}</label>
                  <span class="d-block text-center value">{{ formatModifier(element.details.parry) }}</span>
                </div>
                <div v-if="element.details.cover" class="cover-bonus d-flex flex-column">
                  <label class="d-block text-center fw-semibold">{{ ux.labels?.swade?.data?.equipment?.details?.coverBonus ?? 'Cover' }}</label>
                  <span class="d-block text-center value">{{ formatModifier(element.details.cover) }}</span>
                </div>
                <div v-if="element.details.minStr" class="min-str d-flex flex-column">
                  <label class="d-block text-center fw-semibold">{{ ux.labels?.swade?.data?.equipment?.details?.minStr ?? 'Min Str' }}</label>
                  <span class="d-block text-center value">{{ element.details.minStr }}</span>
                </div>
                <div v-if="element.details.weight" class="weight d-flex flex-column">
                  <label class="d-block text-center fw-semibold">{{ ux.labels?.swade?.data?.equipment?.details?.weight ?? 'Weight' }}</label>
                  <span class="d-block text-center value">{{ element.details.weight }}</span>
                </div>
                <div v-if="element.details.notes" class="notes d-inline-flex gap-2">
                  <label class="d-block text-center fw-semibold mb-auto">{{ ux.labels?.swade?.data?.equipment?.details?.notes ?? 'Notes' }}</label>
                  <span class="d-block value my-auto" v-html="element.details.notes"></span>
                </div>
              </div>

              <div v-if="element.description" class="card-text small mb-3" v-html="element.description"></div>

              <div class="d-flex gap-2 align-items-center">
                <button v-if="isItemAdded(element)" type="button" class="btn btn-outline-danger btn-sm" @click="onRemoveSelectedItem">{{ ux.labels?.guidedSetup?.character?.removeGear ?? 'Remove Item' }}</button>
                <span v-if="itemCount(element) > 0" class="badge bg-info gear-item-count">x{{ itemCount(element) }}</span>
              </div>

              <div v-if="selectedContainer" class="container-details mt-3 border-top pt-3">
                <div class="mb-2">
                  <label class="form-label fw-semibold small">{{ ux.labels?.swade?.data?.equipment?.details?.customName ?? 'Custom Name' }}</label>
                  <input type="text" class="form-control form-control-sm" v-model="selectedContainer.name" :placeholder="selectedContainer.selection?.name" @change="onCustomNameChange(selectedContainer)" />
                </div>
                <div class="mb-2">
                  <label class="form-label fw-semibold small">{{ ux.labels?.swade?.data?.equipment?.quantity ?? 'Quantity' }}</label>
                  <input type="number" class="form-control form-control-sm" v-model.number="selectedContainer.quantity" min="1" step="1" @change="onContainerQuantityChange" />
                </div>
                <div class="mb-2">
                  <label class="form-label fw-semibold small">{{ ux.labels?.swade?.data?.equipment?.details?.charges ?? 'Charges' }}</label>
                  <input type="number" class="form-control form-control-sm" v-model.number="selectedContainer.charges" min="0" :placeholder="ux.labels?.swade?.data?.equipment?.details?.chargesPlaceholder ?? 'N/A'" />
                </div>
                <div class="mb-2">
                  <label class="form-label fw-semibold small">{{ ux.labels?.swade?.data?.equipment?.details?.status ?? 'Status' }}</label>
                  <div class="dropdown">
                    <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                      <span class="fa-light fa-fw" :class="statusIcon(selectedContainer.status)"></span>
                      {{ statusesForType(selectedContainer.selection?.type)?.[selectedContainer.status] }}
                    </button>
                    <ul class="dropdown-menu">
                      <li v-for="(label, key) in statusesForType(selectedContainer.selection?.type)" :key="key">
                        <button type="button" class="dropdown-item" :class="{ active: selectedContainer.status === key }" @click="onContainerStatusChange(key as string)">
                          <span class="fa-light fa-fw" :class="statusIcon(key as string)"></span>
                          {{ label }}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </template>
      </ElementBrowser>
    </div>

    <CurrencyAdjuster
      v-if="currencyContext"
      v-model:character="character"
      :container="currencyContext"
      @finished="onCurrencyAdjusted"
    />
  </div>
</template>
